/**
 * One open VQQ request per vendor and template (US 14958). The server applies the same
 * rule and answers 409 with an `open_request` block; this is the screen-side check that
 * explains the conflict before the request is sent.
 */

export const OPEN_VQQ_STATUSES = Object.freeze([
  "Sent",
  "In Progress",
  "In Review",
  "In Remediation (Vendor)",
  "In Remediation (Purchaser)",
]);

const norm = (value) => String(value ?? "").trim().toLowerCase();

export function isOpenVqqStatus(status) {
  const wanted = norm(status);
  return wanted !== "" && OPEN_VQQ_STATUSES.some((open) => open.toLowerCase() === wanted);
}

function sameTemplate(row, template) {
  const rowId = norm(row?.templateId ?? row?.template_id);
  const optionId = norm(template?.value ?? template?.template_id ?? template?.hash_id);
  if (rowId && optionId) return rowId === optionId;
  const rowName = norm(row?.services ?? row?.template_name);
  const optionName = norm(template?.label);
  return rowName !== "" && rowName === optionName;
}

/**
 * Pairs each picked template with the newest open request the vendor already has for it.
 *
 * @param {Array<object>} openRequests rows from `POST /RFI-Templates/get_rfis` (camelCase)
 * @param {Array<{value?: string, label?: string}>} templates the templates picked on screen
 * @returns {Array<{template: object, request: object}>} in template order; empty when none clash
 */
export function findOpenVqqRequests(openRequests, templates) {
  const open = (openRequests || []).filter((row) => isOpenVqqStatus(row?.status));
  const conflicts = [];
  for (const template of templates || []) {
    if (!template) continue;
    const matches = open
      .filter((row) => sameTemplate(row, template))
      .sort((a, b) => new Date(b?.requestDate ?? 0) - new Date(a?.requestDate ?? 0));
    if (matches.length > 0) conflicts.push({ template, request: matches[0] });
  }
  return conflicts;
}

export function openVqqRequestMessage(vendorName, templateName, status) {
  const vendor = String(vendorName ?? "").trim() || "This vendor";
  const template = String(templateName ?? "").trim() || "this template";
  const state = String(status ?? "").trim() || "open";
  return `${vendor} already has an open request for ${template} (${state}). Cancel it first or pick another template.`;
}

/** One line per clashing template, in the order the templates were picked. */
export function openVqqRequestMessages(vendorName, conflicts) {
  return (conflicts || []).map(({ template, request }) =>
    openVqqRequestMessage(
      vendorName || request?.vendorName || request?.vendor_name,
      template?.label || request?.services,
      request?.status,
    ),
  );
}

/** True when a 409 came from the open-request rule rather than the in-flight duplicate gate. */
export function isOpenVqqRequestConflict(error) {
  return error?.response?.status === 409 && Boolean(error?.response?.data?.open_request);
}
